import { Match, Game } from "../types/firestoreTypes";
import { useSelector } from "react-redux";
import {
  selectCurrentMatch,
  selectCurrentMatchGames,
  selectCurrentMatchId,
  selectCurrentMatchLatestGame,
  selectCurrentMatchPlayers,
  selectMe,
} from "../store/selectors";
import { useFirestore } from "react-redux-firebase";
import { createGame } from "../helpers/createGame";
import { GameStage } from "../constants";
import { useAsyncEffect } from "@react-hook/async";
import { useRef } from "react";

interface ReturnType {
  match: Match | undefined;
  games: Game[];
  joined: boolean;
  openGame: Game | undefined;
}

export const useCurrentMatch = (): ReturnType => {
  const firestore = useFirestore();
  const matchId = useSelector(selectCurrentMatchId);
  const match = useSelector(selectCurrentMatch);
  const games = useSelector(selectCurrentMatchGames);
  const matchPlayers = useSelector(selectCurrentMatchPlayers);
  const me = useSelector(selectMe);
  const latestGame = useSelector(selectCurrentMatchLatestGame);
  
  // Track which gameIndex we're currently creating to prevent duplicates
  const creatingGameIndexRef = useRef<number | null>(null);

  useAsyncEffect(async () => {
    if (
      matchId === undefined ||
      matchPlayers === undefined ||
      matchPlayers.length < 4 ||
      games === undefined ||
      (latestGame !== undefined && latestGame?.stage !== GameStage.Finished)
    ) {
      return;
    }

    const nextGameIndex = latestGame ? latestGame.gameIndex + 1 : 0;
    
    // Check if a game with this index already exists
    const gameWithIndexExists = games.some(g => g.gameIndex === nextGameIndex);
    if (gameWithIndexExists) {
      return;
    }
    
    // Check if we're already creating this game
    if (creatingGameIndexRef.current === nextGameIndex) {
      return;
    }
    
    // Mark that we're creating this game
    creatingGameIndexRef.current = nextGameIndex;
    
    try {
      await createGame({
        matchId,
        gameIndex: nextGameIndex,
        players: matchPlayers,
        firestore,
      });
    } finally {
      // Reset the ref after creation completes (success or failure)
      creatingGameIndexRef.current = null;
    }
  }, [firestore, matchId, matchPlayers, latestGame, games]);

  const joined = matchPlayers?.find((p) => p.id === me?.id) !== undefined;

  return {
    match,
    games: games ?? [],
    joined,
    openGame: games?.find((g) => g.stage !== GameStage.Finished),
  };
};
